import React from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { Calendar } from 'react-feather';
import { format, parse, isValid } from 'date-fns';

import BaseInputWrapper from '../BaseInputWrapper';
import { useDefaultInputHook } from '../hooks';
import InputProps from '../InputProps';
import { generateProperties } from '../helpers';
import colors from '../../../theme/colors';
import { applyDateConstraints } from '../../../functions/dateFormat';

export interface DateFieldProps extends InputProps<Date | string, string> {
  /** If defined, no dates greater than this can be set */
  maxDate?: Date;
  /** If defined, no dates less than this can be set */
  minDate?: Date;
  /** If true, typing a date out of range will automatically force either min or max date */
  autoConstraint?: boolean;
  /**
   * If specified, setting an invalid date (like manually typing it) will report this
   * value in onChange, instead of just not calling it. This is useful for validators,
   * since you can actually catch and show errors for malformed values instead of it
   * not changing inside the app's state.
   */
  fallbackValue: string | undefined;
}

const DateField = ({
  inputId,
  inputLabel,
  inputName,
  onChange,
  defaultValue = new Date(),
  inputError = false,
  inputErrorMessage = '',
  isDisabled = false,
  minDate,
  maxDate,
  fallbackValue = undefined,
  autoConstraint = false,
  dataSource,
}: DateFieldProps): JSX.Element => {
  const formInputProps = generateProperties<Date | string, string>({
    inputName,
    onChange,
    defaultValue,
    inputError,
    inputErrorMessage,
    dataSource,
  });

  let parsedValue = formInputProps.value;

  if (parsedValue instanceof Date) {
    parsedValue = parsedValue.toDateString();
  }

  const { currentClass, onBlur } = useDefaultInputHook({
    error: inputError,
    defaultValue: parsedValue,
  });

  const handleChange = (date: Date | null): void => {
    if (date !== null && isValid(date)) {
      let adjustedDate = date;
      if (autoConstraint) {
        adjustedDate = applyDateConstraints(date, {
          min: minDate,
          max: maxDate,
        });
      }
      const formattedValue = format(adjustedDate, 'dd/MM/yyyy');
      formInputProps.onChange(formattedValue);
    } else if (typeof fallbackValue !== 'undefined') {
      formInputProps.onChange(fallbackValue);
    }
  };

  let dateValue = formInputProps.value;
  if (!(formInputProps.value instanceof Date)) {
    dateValue = parse(formInputProps.value, 'dd/MM/yyyy', new Date());
  }
  if (!isValid(dateValue)) {
    dateValue = new Date();
  }

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <BaseInputWrapper
        inputId={`base-${inputId}`}
        label={inputLabel}
        error={formInputProps.error}
        errorMessage={formInputProps.errorText}
        disabled={isDisabled}
        additionalMargin={10}
      >
        <KeyboardDatePicker
          id={inputId}
          label=" " // <- This prevents the actual label from overlapping with the input
          disableToolbar
          disabled={isDisabled}
          value={dateValue}
          name={formInputProps.name}
          onChange={handleChange}
          className={currentClass}
          format="dd/MM/yyyy"
          variant="inline"
          aria-describedby="component-helper-text"
          maxDate={maxDate}
          minDate={minDate}
          InputLabelProps={{ shrink: true }}
          keyboardIcon={
            <Calendar
              color={isDisabled ? colors.silver : colors.bright_turquoise}
              size={20}
            />
          }
          onBlur={onBlur}
          helperText=""
        />
      </BaseInputWrapper>
    </MuiPickersUtilsProvider>
  );
};

export default DateField;
